import { Injectable } from '@angular/core';
import { HttpClient, HttpErrorResponse, HttpHeaders, HttpParams } from '@angular/common/http';
import { BehaviorSubject, Observable, throwError } from 'rxjs';
import { catchError, first, map } from 'rxjs/operators';
import { Router } from '@angular/router';
import { API_URL_PREFIX, API_VERSION, ENTRY_POINT, IDLE_MAX_MIN } from '../constants';
import { clearCookie } from '../utils/cookie';

let checkActiveTimers: number[] = [];
@Injectable({
  providedIn: 'root', // Makes the service available application-wide
})
export class HttpService {
  public state: { [key: string]: any } = {
    loading$: new BehaviorSubject<boolean>(false)
  };
  private readonly defaultHeaders = new HttpHeaders({
    'Content-Type': 'application/json',
  });
  public router: Router;
  public devMode: boolean = location.hostname === 'localhost';
  // Time of last active Http request
  private lastActive: number;

  constructor(private http: HttpClient) { }

  /**
  * Update an observable and emit a new value
  */
  public updateObservable<T>(
    subject: BehaviorSubject<T>,
    callback: (p: T) => T
  ) {
    subject.pipe(first()).subscribe((project) => {
      subject.next(callback(project) as T);
    });
  }

  /**
   * Generic GET request
   * @param url The endpoint URL
   * @param params Optional query parameters
   * @param headers Optional custom headers
   */
  get<T>(url: string, params?: { [key: string]: any }, headers?: HttpHeaders): Observable<T> {
    headers = this.beforeRequest(headers || new HttpHeaders(), url);
    return this.http
      .get<T>(url, { headers, params })
      .pipe(
        // Remove this line because response is already the actual data
        // map((response: any) => response.data),
        catchError((error) => this.handleError(error))
      );
  }

  /**
   * Generic POST request
   * @param url The endpoint URL
   * @param body The request payload
   * @param headers Optional custom headers
   */
  post<T>(url: string, body: any,
    headers?: HttpHeaders): Observable<T> {
    headers = this.beforeRequest(headers || new HttpHeaders(), url);
    return this.http
      .post<T>(url, body, { headers: headers || this.defaultHeaders })
      .pipe(
        map((response: any) => response.data),
        catchError((error) => this.handleError(error)));
  }

  /**
   * Generic PUT request
   * @param url The endpoint URL
   * @param body The request payload
   * @param headers Optional custom headers
   */
  put<T>(url: string, body: any, headers?: HttpHeaders): Observable<T> {
    headers = this.beforeRequest(headers || new HttpHeaders(), url);
    return this.http
      .put<T>(url, body, { headers: headers || this.defaultHeaders })
      .pipe(catchError((error) => this.handleError(error)));
  }

  /**
   * Generic DELETE request
   * @param url The endpoint URL
   * @param params Optional query parameters
   * @param headers Optional custom headers
   */
  delete<T>(url: string, params?: HttpParams, headers?: HttpHeaders): Observable<T> {
    headers = this.beforeRequest(headers || new HttpHeaders(), url);
    return this.http
      .delete<T>(url, { headers: headers || this.defaultHeaders, params })
      .pipe(catchError((error) => this.handleError(error)));
  }

  /**
   * Generic PATCH request
   * @param url The endpoint URL
   * @param params Optional query parameters
   * @param headers Optional custom headers
   */
  patch<T>(url: string, params?: HttpParams, headers?: HttpHeaders): Observable<T> {
    headers = this.beforeRequest(headers || new HttpHeaders(), url);
    return this.http
      .patch<T>(url, { headers: headers || this.defaultHeaders, params })
      .pipe(catchError((error) => this.handleError(error)));
  }

  /**
   * Error handler for HTTP requests
   * @param error The HTTP error response
   */
  private handleError(error: HttpErrorResponse): Observable<never> {
    if (this.state && this.state.loading$) {
      this.state.loading$.next(false);
    }
    let errorMessage = 'An unknown error occurred!';
    this.handleErrorStatus(error);
    return throwError(() => error.error || errorMessage);

  }

  /**
  * Deal with error status codes
  */
  public handleErrorStatus(err: HttpErrorResponse) {
    switch (err.status) {
      // redirect to login page if access denied
      case 401:
        this.clearCheckActiveTimer();

        // const redirect = this.parseRedirectUrl(err);

        // if (redirect) {
        //   location.replace(redirect);
        // } else {
        //   this.router.navigate(['/login']);
        // }

        break;

      // trace log and logout if no permission
      case 403:
        if (location.hostname !== 'localhost') {
          this.http
            .post('/api/statistics', {
              href: '',
              path: '',
              action: '',
              statusCode: 403
            })
            .subscribe();
        }

        this.autoLogout();
        break;

      // Call a `patchMe` after 404
      // Backend may return 404 code after session expires
      case 404:
        let url = 'me';
        this.http.patch(API_URL_PREFIX + url, {
          apiVersion: API_VERSION,
          entryPoint: ENTRY_POINT
        }).subscribe();
        break;

      // redirect to maintenance page
      case 502:
      case 504:
        this.router.navigate(['/common/maintenance']);
        break;
      default:
    }
  }

  /**
   * Parse redirect URL from 401 error
   */
  // private parseRedirectUrl(res: HttpErrorResponse): string {
  //   if ((window as any).unsupportBrowser) {
  //     return;
  //   }

  //   if (!res || !res.error || !res.error.message) {
  //     return;
  //   }

  //   const locationReg = /^Location:/;

  //   if (!locationReg.test(res.error.message)) {
  //     return;
  //   }

  //   return res.error.message.replace(locationReg, '');
  // }
  /**
  * Do something to headers before sending request
  */
  public beforeRequest(headers: HttpHeaders, url?: string): HttpHeaders {
    this.lastActive = Date.now();
    this.checkActive();
    if (this.state.loading$) {
      this.state.loading$.next(true);
    }

    if (this.devMode) {
      const token ="";
      headers = new HttpHeaders({
        'x-auth-token': token,
        Authorization: `Bearer ${token}`
      });
    }
    return headers;
  }

  /**
   * Auto logout after 30 min inactivity
   */
  private checkActive() {
    if (!this.lastActive) {
      return;
    }

    if (Date.now() - this.lastActive >= IDLE_MAX_MIN) {
      this.autoLogout();
    } else {
      this.clearCheckActiveTimer();

      checkActiveTimers.push(
        window.setTimeout(() => {
          this.checkActive();
        }, IDLE_MAX_MIN)
      );
    }
  }
  /**
   * Log out
   */
  private autoLogout() {
    this.http.delete('/api/call/login').subscribe(() => {
      this.clearCheckActiveTimer();

      clearCookie();

      this.router.navigate(['/login']);
    });
  }

  /**
   * Clear check active timer
   */
  private clearCheckActiveTimer() {
    checkActiveTimers.forEach((t) => {
      window.clearTimeout(t);
    });

    checkActiveTimers = [];
  }

}